export type OAuthReturnMode = "token" | "cookie";

export type OAuthReturnModeResolution =
  | { ok: true; mode: OAuthReturnMode }
  | { ok: false; mode: OAuthReturnMode; error: string };

const DEFAULT_ALLOWED_RETURN_MODES: OAuthReturnMode[] = ["token"];
const OAUTH_RETURN_MODES = new Set<OAuthReturnMode>(["token", "cookie"]);

function isOAuthReturnMode(value: unknown): value is OAuthReturnMode {
  return typeof value === "string" && OAUTH_RETURN_MODES.has(value as OAuthReturnMode);
}

function normalizeRequestedOAuthReturnMode(value: unknown): OAuthReturnMode {
  return isOAuthReturnMode(value) ? value : "token";
}

function getAllowedReturnModes(config: unknown): unknown {
  if (typeof config !== "object" || config === null) return undefined;
  return (config as { allowedReturnModes?: unknown }).allowedReturnModes;
}

function normalizeAllowedOAuthReturnModes(config?: unknown): OAuthReturnMode[] {
  const allowedReturnModes = getAllowedReturnModes(config);
  if (!Array.isArray(allowedReturnModes)) return DEFAULT_ALLOWED_RETURN_MODES;
  return allowedReturnModes.filter(isOAuthReturnMode);
}

export function resolveOAuthReturnMode(
  requestedMode: unknown,
  config?: unknown,
): OAuthReturnModeResolution {
  const mode = normalizeRequestedOAuthReturnMode(requestedMode);
  const allowedModes = normalizeAllowedOAuthReturnModes(config);
  if (allowedModes.includes(mode)) return { ok: true, mode };

  return {
    ok: false,
    mode,
    error: `OAuth return mode "${mode}" is not allowed`,
  };
}
